import { GlobalContext } from "global/context/Provider";
import { showToaster } from "global/context/action/toaster/toaster";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import Loading from "_+_Portal/components/Genericos/Loading/Loading";
import LayoutPasos from "_+_Portal/_-_Autocheckin/components/genericos/LayoutPasos/LayoutPasos";
import CardBlueHeart from "_+_Portal/_-_Autocheckin/components/genericos/CardBlueHeart/CardBlueHeart";
import PasoBar from "_+_Portal/_-_Autocheckin/components/genericos/PasoBar/PasoBar";
import { setPaso } from "_+_Portal/_-_Autocheckin/context/action/paso";
import { setRuta } from "_+_Portal/_-_Autocheckin/context/action/ruta";
import {
  wsAsociarOrdenTurno,
  wsGetMatchOrdenTurno,
} from "_+_Portal/_-_Autocheckin/context/action/orden";
import { wsGetTurnoByIdQr } from "_+_Portal/_-_Autocheckin/context/action/turnosAutoCheckIn";
import { setIdTipoPuntoControl } from "_+_Portal/_-_Autocheckin/context/action/puntoControl";
import { AutocheckContext } from "_+_Portal/_-_Autocheckin/context/Provider";
import { proximoPaso } from "../PagesFun";
import camelize from "global/utils/camelize";
import ResumenLista from "_+_Portal/_-_Autocheckin/components/genericos/ResumenLista/ResumenLista";
import TildeOk from "_+_Portal/assets/images/generico/TildeOk";
import { funAcortarText } from "global/utils/text";
import { getLocalStorage } from "global/utils/safeJsonParse";
import {
  ContainerSeleccionOrden,
  HeaderTexto,
  AccionesPie,
  TituloPrestaciones,
  PrestacionesCompact,
  CardOrdenSelectable,
  CardOrdenTop,
  CardOrdenBody,
  ListaOrdenes,
} from "./localStyle";

// Devuelve true si los items de las órdenes dadas cubren todos los nomencladores del turno.
const cubreTodo = (ordenes, nomencladores) => {
  if (nomencladores.length === 0) return false;
  return nomencladores.every((nom) =>
    ordenes.some((ord) =>
      (ord.items || []).some((it) =>
        (nom.idsGruposMedicos || []).includes(it.idGrupoEstudio),
      ),
    ),
  );
};

const SeleccionOrden = ({ turnoActual, setLoading }) => {
  const { toasterDispatch } = useContext(GlobalContext);
  const {
    turnosSeleccionadosState,
    ordenDispatch,
    rutaState,
    pasoDispatch,
    rutaDispatch,
    puntoControlDispatch,
  } = useContext(AutocheckContext);

  const listTurnosSeleccionado =
    turnosSeleccionadosState.turnosSeleccionados?.data ?? [];
  const turno = listTurnosSeleccionado[turnoActual];

  // En terminal los textos van más grandes para mejor legibilidad a distancia.
  const idTerminal = getLocalStorage("idTerminal");
  const isTerminal = !!idTerminal;

  const [ordenes, setOrdenes] = useState([]);
  const [nomencladoresTurno, setNomencladoresTurno] = useState([]);
  const [seleccionadas, setSeleccionadas] = useState(() => new Set());
  const [loadingMatch, setLoadingMatch] = useState(true);
  const [enviando, setEnviando] = useState(false);

  // Guard de idempotencia: cualquier transición de paso desde SeleccionOrden debe ocurrir
  // UNA sola vez. Sin esto, un re-mount o doble dispatch del useEffect ejecutaría dos
  // veces irFlujoComun y haría dos shifts seguidos sobre rutaState — el primer shift
  // setea MedicoSolicitante, el segundo lo saltea y deriva a ValidarCobertura. Este bug
  // ya había ocurrido antes y volvió a aparecer.
  const flujoAvanzado = useRef(false);

  // Sin match digital — paciente sin órdenes pendientes que matcheen. Continúa el flujo
  // viejo (MedicoSolicitante → SubirOrden). El paso SeleccionOrden ya fue consumido de
  // la ruta por Resumen.handleContinuar antes de entrar acá; proximoPaso hace el shift
  // del siguiente paso. Un shift extra acá saltaría MedicoSolicitante y derivaría a
  // ValidarCobertura por error.
  const irFlujoComun = () => {
    if (flujoAvanzado.current) {
      console.log("[FLUJO] SeleccionOrden.irFlujoComun - YA EJECUTADO, ignorando (idempotencia)");
      return;
    }
    flujoAvanzado.current = true;
    const nuevaRuta = [...rutaState.ruta.data];
    console.log("[FLUJO] SeleccionOrden.irFlujoComun - ruta antes del shift:", nuevaRuta);
    setRuta(proximoPaso(nuevaRuta, setPaso, pasoDispatch))(rutaDispatch);
    console.log("[FLUJO] SeleccionOrden.irFlujoComun - ruta despues del shift:", nuevaRuta);
  };

  // Deriva el turno a Punto de Control. Tres situaciones lo disparan:
  //  1. Paciente no marca ninguna pendiente (rechazo).
  //  2. Cobertura asignadas+seleccionadas parcial (mostrador resuelve el faltante).
  //  3. Re-entrada con asignadas previas que NO cubren todo y NO hay pendientes que aporten.
  const enviarPuntoControl = () => {
    if (flujoAvanzado.current) return;
    flujoAvanzado.current = true;
    setLoading(true);
    setIdTipoPuntoControl(5)(puntoControlDispatch);
    setPaso("PuntoControl")(pasoDispatch);
  };

  // Mismo patrón que SubirOrden.siguientePaso: refresca el turno y arma el resto de la
  // ruta. Cuando la asociación con orden digital salteó SubirOrden, la firma del paciente
  // (que en el flujo viejo es sub-step interno de SubirOrden) se pide acá como paso
  // dedicado FirmaOrdenDigital — salvo que el turno tenga sinFirmaPacOrdenTurno.
  const siguientePaso = () => {
    if (flujoAvanzado.current) return;
    flujoAvanzado.current = true;
    const ruta = rutaState.ruta.data;
    const requiereFirma =
      !turno?.sinFirmaPacOrdenTurno && !turno?.flgSoloConsulta;

    const nextStepTurnoById = (isCorrect, data) => {
      if (!isCorrect) {
        enviarPuntoControl();
        return;
      }
      const pathUpdated = [];
      if (requiereFirma) {
        pathUpdated.push("FirmaOrdenDigital");
      }
      if (
        data.resumen.validaOnline ||
        data.resumen.validaOnlineEstado === "Pendiente"
      ) {
        pathUpdated.push("ValidarCobertura");
      }
      if (data?.resumen?.monto) {
        pathUpdated.push("AbonarMP");
      }
      pathUpdated.push(ruta.pop());
      setRuta(proximoPaso(pathUpdated, setPaso, pasoDispatch))(rutaDispatch);
    };

    setLoading("Actualizando información de turno...");
    // wsGetTurnoByIdQr NO es curried (retorna void). No aplicar (dispatch).
    wsGetTurnoByIdQr(turno?.idturno, nextStepTurnoById);
  };

  const toggleOrden = (idOrden) => {
    if (enviando) return;
    setSeleccionadas((prev) => {
      const next = new Set(prev);
      if (next.has(idOrden)) next.delete(idOrden);
      else next.add(idOrden);
      return next;
    });
  };

  // Cobertura efectiva: items de las órdenes ASIGNADAS (siempre cuentan, no se pueden
  // desmarcar) + items de las pendientes que el paciente marcó.
  const { coberturaPorNomenclador, estadoBoton, textoBoton, ordenesAsignadas, ordenesPendientes } = useMemo(() => {
    const ordenesAsignadas = ordenes.filter((o) => o.asignada);
    const ordenesPendientes = ordenes.filter((o) => !o.asignada);

    const ordenesEfectivas = [
      ...ordenesAsignadas,
      ...ordenesPendientes.filter((o) => seleccionadas.has(o.idOrden)),
    ];

    const coberturaPorNomenclador = nomencladoresTurno.map((nom) => ({
      ...nom,
      cubierto: ordenesEfectivas.some((ord) =>
        (ord.items || []).some((it) =>
          (nom.idsGruposMedicos || []).includes(it.idGrupoEstudio),
        ),
      ),
    }));

    const algoSeleccionadoNuevo = seleccionadas.size > 0;
    const coberturaTotal =
      nomencladoresTurno.length > 0 &&
      coberturaPorNomenclador.every((n) => n.cubierto);

    // "vacio" = sin nada nuevo seleccionado (puede haber asignadas previas igual). En ese
    // caso el único action posible es ir a PC porque sabemos por useEffect que las
    // asignadas solas no cubren todo (si lo hicieran ya habríamos avanzado).
    const estadoBoton = !algoSeleccionadoNuevo
      ? "vacio"
      : coberturaTotal
        ? "total"
        : "parcial";

    const textoBoton = {
      vacio: "Ir a punto de control",
      total: "Asignar y continuar",
      parcial: "Asignar y derivar a punto de control",
    }[estadoBoton];

    return { coberturaPorNomenclador, estadoBoton, textoBoton, ordenesAsignadas, ordenesPendientes };
  }, [ordenes, nomencladoresTurno, seleccionadas]);

  const handleConfirmar = () => {
    if (enviando) return;

    if (estadoBoton === "vacio") {
      enviarPuntoControl();
      return;
    }

    setEnviando(true);
    setLoading("Asignando órdenes al turno...");
    // Solo enviar las pendientes marcadas — las ya asignadas el backend las skipea
    // pero no tiene sentido mandarlas.
    const ids = Array.from(seleccionadas);

    wsAsociarOrdenTurno(turno?.idturno, ids, (isCorrect, err) => {
      if (!isCorrect) {
        setEnviando(false);
        setLoading(false);
        const msg =
          err?.response?.data?.message ||
          err?.response?.data ||
          "No se pudo asignar la orden. Intentá nuevamente o pasá por mostrador.";
        showToaster(
          {
            texto: typeof msg === "string" ? msg : "Error al asignar la orden",
            tipo: "danger",
          },
          "centroArriba",
        )(toasterDispatch);
        return;
      }
      if (estadoBoton === "parcial") {
        enviarPuntoControl();
      } else {
        siguientePaso();
      }
    })(ordenDispatch);
  };

  useEffect(() => {
    console.log("[FLUJO] SeleccionOrden.useEffect MOUNT - turno.idturno:", turno?.idturno, "ruta actual:", rutaState.ruta.data);
    if (!turno?.idturno) {
      console.log("[FLUJO] SeleccionOrden - sin idturno -> irFlujoComun");
      irFlujoComun();
      return;
    }
    wsGetMatchOrdenTurno(turno.idturno, (isCorrect, data) => {
      console.log("[FLUJO] SeleccionOrden.wsGetMatchOrdenTurno callback - isCorrect:", isCorrect, "data:", data);
      if (!isCorrect) {
        setLoadingMatch(false);
        console.log("[FLUJO] SeleccionOrden - match falló -> irFlujoComun");
        irFlujoComun();
        return;
      }
      const ordenesResp = Array.isArray(data?.ordenes) ? data.ordenes : [];
      const nomencladoresResp = Array.isArray(data?.nomencladoresTurno)
        ? data.nomencladoresTurno
        : [];

      // No hay match (ni asignadas ni pendientes que aporten) — paciente sin orden
      // digital relevante. Continúa flujo viejo (MedSol + SubirOrden).
      if (ordenesResp.length === 0 || nomencladoresResp.length === 0) {
        setLoadingMatch(false);
        console.log("[FLUJO] SeleccionOrden - ordenes/nomencladores vacios -> irFlujoComun");
        irFlujoComun();
        return;
      }

      const asignadas = ordenesResp.filter((o) => o.asignada);
      const pendientes = ordenesResp.filter((o) => !o.asignada);

      // Re-entrada: si las órdenes ya asignadas cubren todos los nomencladores, no hay
      // que mostrar SeleccionOrden ni preguntarle al paciente — avanzar a Validar.
      if (asignadas.length > 0 && cubreTodo(asignadas, nomencladoresResp)) {
        siguientePaso();
        return;
      }

      // Si combinando todo lo disponible (asignadas + pendientes) es IMPOSIBLE cubrir
      // los nomencladores del turno, derivar a PC directo. El paciente no tiene cómo
      // completar — mostrador resuelve.
      if (!cubreTodo([...asignadas, ...pendientes], nomencladoresResp)) {
        enviarPuntoControl();
        return;
      }

      // Cobertura completable: mostrar pantalla con asignadas como "ya cubierto"
      // y permitir marcar pendientes que aporten al gap.
      setOrdenes(ordenesResp);
      setNomencladoresTurno(nomencladoresResp);
      setLoadingMatch(false);
    })(ordenDispatch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loadingMatch) {
    return (
      <Loading
        dataLoading={true}
        descripcion="Buscando órdenes digitales..."
        color={"c-white"}
      />
    );
  }

  const renderCardAsignada = (o) => {
    const fechaFmt = new Date(o.fecha).toLocaleDateString("es-AR");
    const itemsTxt = (o.items || [])
      .map((i) => camelize(i.descripcion))
      .join(" + ");
    return (
      <CardOrdenSelectable key={o.idOrden} className="selected fija">
        <CardOrdenTop $active={true}>
          <div className="circle-orange">
            <TildeOk color={"var(--color-white)"} />
          </div>
          <h1>{funAcortarText(25, itemsTxt || "Orden digital")}</h1>
          <span className="badge-asignada">Ya asociada</span>
        </CardOrdenTop>
        <CardOrdenBody>
          <p className="rb18b">
            Emitida el: <span className="rb18l">{fechaFmt}</span>
          </p>
          {o.medicoSolicitante && (
            <p className="rb18b">
              Médico:{" "}
              <span className="rb18l">
                {funAcortarText(25, o.medicoSolicitante)}
              </span>
            </p>
          )}
        </CardOrdenBody>
      </CardOrdenSelectable>
    );
  };

  const renderCardPendiente = (o) => {
    const fechaFmt = new Date(o.fecha).toLocaleDateString("es-AR");
    const checked = seleccionadas.has(o.idOrden);
    const itemsTxt = (o.items || [])
      .map((i) => camelize(i.descripcion))
      .join(" + ");
    return (
      <CardOrdenSelectable
        key={o.idOrden}
        className={checked ? "selected" : ""}
        onClick={() => !enviando && toggleOrden(o.idOrden)}
      >
        <CardOrdenTop $active={checked}>
          <div className="circle-orange">
            {checked && <TildeOk color={"var(--color-white)"} />}
          </div>
          <h1>{funAcortarText(25, itemsTxt || "Orden digital")}</h1>
        </CardOrdenTop>
        <CardOrdenBody>
          <p className="rb18b">
            Emitida el: <span className="rb18l">{fechaFmt}</span>
          </p>
          {o.medicoSolicitante && (
            <p className="rb18b">
              Médico:{" "}
              <span className="rb18l">
                {funAcortarText(25, o.medicoSolicitante)}
              </span>
            </p>
          )}
        </CardOrdenBody>
      </CardOrdenSelectable>
    );
  };

  return (
    <LayoutPasos
      volverTo={"/autocheckin/resumen"}
      handleClick={() => setPaso("TurnosDelDia")(pasoDispatch)}
      type={"no-circle"}
      title={"orden médica"}
      children={
        <>
          <CardBlueHeart
            medico={turno?.medico ? turno.medico : turno?.especialidad}
            turnoTitle={turno?.especialidad}
            pasoActual={turnoActual + 1}
            pasoTotal={listTurnosSeleccionado.length}
          />
          <ContainerSeleccionOrden
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 100 }}
            transition={{ duration: 0.4 }}
          >
            <PasoBar paso={"SubirOrden"} />

            <HeaderTexto $isTerminal={isTerminal}>
              <span className={isTerminal ? "rb24m" : "rb16m"}>
                {ordenesAsignadas.length > 0
                  ? "Necesitamos cubrir todos los estudios del turno. Marcá las órdenes pendientes que correspondan."
                  : "Encontramos órdenes digitales que coinciden con tu turno. Marcá las que son tuyas para asociarlas al turno."}
              </span>
            </HeaderTexto>

            {nomencladoresTurno.length > 0 && (
              <>
                <TituloPrestaciones $isTerminal={isTerminal}>
                  Estudios del turno
                </TituloPrestaciones>
                <PrestacionesCompact $isTerminal={isTerminal}>
                  <ResumenLista
                    lista={coberturaPorNomenclador.map((nom) => ({
                      estado: nom.cubierto,
                      nombre: camelize(nom.descripcion),
                    }))}
                  />
                </PrestacionesCompact>
              </>
            )}

            <ListaOrdenes>
              {ordenesAsignadas.map(renderCardAsignada)}
              {ordenesPendientes.map(renderCardPendiente)}
            </ListaOrdenes>

            <AccionesPie>
              <button
                className="btn-principal rb16m"
                disabled={enviando}
                onClick={handleConfirmar}
              >
                {enviando ? "Procesando..." : textoBoton}
              </button>
              {estadoBoton !== "vacio" && (
                <button
                  className="btn-secundario rb16m"
                  onClick={() => setSeleccionadas(new Set())}
                  disabled={enviando}
                >
                  Limpiar selección
                </button>
              )}
            </AccionesPie>
          </ContainerSeleccionOrden>
        </>
      }
    />
  );
};

export default SeleccionOrden;
